import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import type { ServiceAccount, ServiceAccountInput } from '@cms/models';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/ui/toggle';
import { SearchableSelect } from '@/components/custom/forms/SearchableSelect';
import { CommonModal } from '@/components/common/CommonModal';
import { cn } from '@/lib/utils';
import { saasApi } from '@/lib/api/saas.api';
import { extractApiErrorMessage } from '@/lib/api/extract-api-error-message';
import { useCreateServiceAccount, useUpdateServiceAccount } from '@/lib/api/service-accounts';

type ServiceAccountFormData = {
  name: string;
  platform_code: string;
  tenant_id: string;
  status: 'active' | 'inactive';
  username: string;
  password: string;
  cookies: string;
  notes: string;
};

const inputClass =
  'mt-1 block w-full h-9 px-3 py-1.5 text-sm bg-background border border-border rounded-md focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors';

const textareaClass =
  'mt-1 block w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors resize-y';

const AI_PLATFORM_CODE_OPTIONS = [
  { value: 'gemini', label: 'Gemini' },
  { value: 'google', label: 'Google' },
  { value: 'chatgpt', label: 'ChatGPT' },
  { value: 'claude', label: 'Claude' },
  { value: 'grok', label: 'Grok' },
  { value: 'copilot', label: 'Copilot' },
  { value: 'midjourney', label: 'Midjourney' },
  { value: 'veo', label: 'Veo' },
  { value: 'runway', label: 'Runway' },
  { value: 'stable-diffusion', label: 'Stable Diffusion' },
  { value: 'dalle', label: 'DALL-E' },
  { value: 'perplexity', label: 'Perplexity' },
  { value: 'qwen', label: 'Qwen' },
] as const;

export type ServiceAccountFormModalMode = 'create' | 'view' | 'edit';

interface ServiceAccountFormModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly mode: ServiceAccountFormModalMode;
  readonly serviceAccount: ServiceAccount | null;
  readonly onSuccess?: () => void;
}

export function ServiceAccountFormModal({
  open,
  onClose,
  mode,
  serviceAccount,
  onSuccess,
}: ServiceAccountFormModalProps) {
  const formId = 'service-account-form';
  const isCreate = mode === 'create';
  const isView = mode === 'view';
  const { data: tenants = [] } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => saasApi.getTenants(),
  });
  const tenantOptions = useMemo(
    () =>
      tenants.reduce<Array<{ value: string; label: string }>>((acc, tenant) => {
        if (!tenant.id) return acc;
        acc.push({
          value: tenant.id,
          label: tenant.name ?? tenant.code ?? tenant.id,
        });
        return acc;
      }, []),
    [tenants],
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ServiceAccountFormData>({
    defaultValues: {
      name: '',
      platform_code: '',
      tenant_id: '',
      status: 'active',
      username: '',
      password: '',
      cookies: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (serviceAccount) {
      reset({
        name: serviceAccount.name ?? '',
        platform_code: serviceAccount.platform_code ?? '',
        tenant_id: serviceAccount.tenant_id ?? '',
        status: serviceAccount.status === 'inactive' ? 'inactive' : 'active',
        username: serviceAccount.username ?? '',
        password: serviceAccount.password ?? '',
        cookies: serviceAccount.cookies ?? '',
        notes: serviceAccount.notes ?? '',
      });
      return;
    }
    if (isCreate) {
      reset({
        name: '',
        platform_code: '',
        tenant_id: '',
        status: 'active',
        username: '',
        password: '',
        cookies: '',
        notes: '',
      });
    }
  }, [serviceAccount, isCreate, reset]);

  const createMutation = useCreateServiceAccount();
  const updateMutation = useUpdateServiceAccount();

  const onSubmit = async (data: ServiceAccountFormData) => {
    if (isView) return;
    const payload: ServiceAccountInput = {
      name: data.name.trim(),
      platform_code: data.platform_code.trim(),
      tenant_id: data.tenant_id.trim() || undefined,
      status: data.status,
      username: data.username.trim() || undefined,
      password: data.password.trim() || undefined,
      cookies: data.cookies.trim() || undefined,
      notes: data.notes.trim() || undefined,
    };
    try {
      if (isCreate) {
        await createMutation.mutateAsync(payload);
        toast.success('AI Account created successfully.');
      } else if (serviceAccount?.id) {
        await updateMutation.mutateAsync({ id: serviceAccount.id, payload });
        toast.success('AI Account updated successfully.');
      }
      onSuccess?.();
      onClose();
    } catch (error: unknown) {
      toast.error(extractApiErrorMessage(error));
    }
  };

  let title = 'Edit AI Account';
  if (mode === 'create') {
    title = 'Create AI Account';
  } else if (mode === 'view') {
    title = 'AI Account details';
  }

  let submitButtonLabel = 'Save';
  if (isCreate) {
    submitButtonLabel = createMutation.isPending ? 'Creating...' : 'Create';
  } else if (updateMutation.isPending) {
    submitButtonLabel = 'Saving...';
  }

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={title}
      titleId="service-account-form-modal-title"
      panelClassName="max-w-2xl"
      overlayClassName="p-0"
      footer={
        isView ? (
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              form={formId}
              type="submit"
              disabled={isSubmitting || (isCreate ? createMutation.isPending : updateMutation.isPending)}
            >
              {submitButtonLabel}
            </Button>
          </>
        )
      }
      footerClassName="flex justify-end gap-2"
    >
      <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label htmlFor="service-account-name" className="block text-sm font-medium text-foreground">
                Name *
              </label>
              <input
                id="service-account-name"
                type="text"
                {...register('name', { required: 'Name is required' })}
                disabled={isView}
                className={cn(inputClass, isView && 'cursor-default bg-muted/50')}
              />
              {!isView && errors.name && <p className="mt-1 text-sm text-destructive">{errors.name.message}</p>}
            </div>
            <div>
              <label htmlFor="service-account-platform-code" className="block text-sm font-medium text-foreground">
                Platform Code *
              </label>
              {isView ? (
                <input
                  id="service-account-platform-code"
                  type="text"
                  value={
                    AI_PLATFORM_CODE_OPTIONS.find(option => option.value === (serviceAccount?.platform_code ?? ''))
                      ?.label ??
                    serviceAccount?.platform_code ??
                    ''
                  }
                  disabled
                  className={cn(inputClass, 'cursor-default bg-muted/50')}
                />
              ) : (
                <Controller
                  name="platform_code"
                  control={control}
                  rules={{ required: 'Platform code is required' }}
                  render={({ field }) => (
                    <SearchableSelect
                      id="service-account-platform-code"
                      options={AI_PLATFORM_CODE_OPTIONS.map(option => ({ value: option.value, label: option.label }))}
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      placeholder="Search platform..."
                      allowCustomValue
                      className="mt-1"
                    />
                  )}
                />
              )}
              {!isView && errors.platform_code && (
                <p className="mt-1 text-sm text-destructive">{errors.platform_code.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label htmlFor="service-account-tenant-id" className="block text-sm font-medium text-foreground">
                Tenant ID
              </label>
              {isView ? (
                <input
                  id="service-account-tenant-id"
                  type="text"
                  value={
                    tenantOptions.find(option => option.value === (serviceAccount?.tenant_id ?? ''))?.label ??
                    serviceAccount?.tenant_id ??
                    ''
                  }
                  disabled
                  className={cn(inputClass, 'cursor-default bg-muted/50')}
                />
              ) : (
                <Controller
                  name="tenant_id"
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      id="service-account-tenant-id"
                      options={tenantOptions}
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      placeholder="Search tenant..."
                      emptyOption={{ value: '', label: 'No tenant' }}
                      className="mt-1"
                    />
                  )}
                />
              )}
            </div>
            <div className="flex items-end">
              {isView ? (
                <div>
                  <span className="block text-sm font-medium text-muted-foreground mb-1">Status</span>
                  <span className="inline-flex items-center rounded-md border border-border px-2.5 py-1 text-sm capitalize">
                    {serviceAccount?.status ?? '—'}
                  </span>
                </div>
              ) : (
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Toggle
                      id="service-account-status"
                      label="Status"
                      checked={field.value === 'active'}
                      onCheckedChange={checked => field.onChange(checked ? 'active' : 'inactive')}
                    />
                  )}
                />
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label htmlFor="service-account-username" className="block text-sm font-medium text-foreground">
                Username
              </label>
              <input
                id="service-account-username"
                type="text"
                {...register('username')}
                disabled={isView}
                className={cn(inputClass, isView && 'cursor-default bg-muted/50')}
              />
            </div>
            <div>
              <label htmlFor="service-account-password" className="block text-sm font-medium text-foreground">
                Password
              </label>
              <input
                id="service-account-password"
                type="text"
                {...register('password')}
                disabled={isView}
                className={cn(inputClass, isView && 'cursor-default bg-muted/50')}
              />
            </div>
          </div>

          <div>
            <label htmlFor="service-account-cookies" className="block text-sm font-medium text-foreground">
              Cookies
            </label>
            <textarea
              id="service-account-cookies"
              rows={6}
              spellCheck={false}
              {...register('cookies')}
              disabled={isView}
              className={cn(textareaClass, 'font-mono', isView && 'cursor-default bg-muted/50')}
            />
          </div>

          <div>
            <label htmlFor="service-account-notes" className="block text-sm font-medium text-foreground">
              Notes
            </label>
            <textarea
              id="service-account-notes"
              rows={3}
              {...register('notes')}
              disabled={isView}
              className={cn(textareaClass, isView && 'cursor-default bg-muted/50')}
            />
          </div>

      </form>
    </CommonModal>
  );
}
